// What a blow looks like where it lands, per attacker (fx `hit`'s `by`): a knight's mace cracks the ground, Дюрер's
// blade rings on metal, Смерть's scythe leaves one long stroke… Ink like everything else: the impacts differ by
// shape and motion, not glow, and each is over in well under a second. fx.ts owns the lookup (an attacker kind it
// has no entry for, or none at all, keeps the plain ring and flash) and works out `d`; this table only builds and moves.
import * as THREE from 'three';
import { mk, flat } from './engrave.js';
import type { BoltKit } from './fxBolts.js';

export type ImpactKit = Pick<BoltKit, 'spawn' | 'puff' | 'fxMesh' | 'ink' | 'crow'>;

/** `at`: the victim's spot on the ground; `d`: the unit ground direction the blow travelled (attacker → victim,
    a random one when the attacker is not in sight). Both are scratch: copy what is kept. `color`: the event's
    colour, which the ranged impacts take as their own. */
export type Impact = (at: THREE.Vector3, d: THREE.Vector3, big: boolean, color: number) => void;

// The game camera looks down along matchView.ts's OFFSET (0, 21, 15.5): strokes drawn "across" a victim are
// tilted to face it, and lifted toward it so the body does not swallow them.
export const FACE = -Math.atan2(21, 15.5), TOWARD = 0.9;
const FRONT = 0.6;
const DUST = 0xcbbf9f, CLOD = 0x4a3826, SPARK = 0xf0c048, STEEL = 0xd8dce0, SCYTHE = 0x281c30, SPRAY = 0x8aa4b8,
  CHAIN = 0x6a6e78, CLAW_RED = 0x6a1616, CLAW_BLACK = 0x2a1210, EMBER = 0xb8482a, CLAY = 0x8a6a4a, TAWNY = 0xb89a6a;

const rnd = (a: number, b: number) => a + Math.random() * (b - a);
function shade(c: number, k: number) {
  const ch = (s: number) => Math.min(255, Math.round(((c >> s) & 255) * k)) << s;
  return ch(16) | ch(8) | ch(0);
}

/* A brush stroke along +x from 0 to `len`: pointed ends, `w` across at its fattest, bowed by `bend` × len toward +y. */
export function strokeGeometry(len: number, w: number, bend: number) {
  const top: THREE.Vector2[] = [], bot: THREE.Vector2[] = [], n = 14;
  for (let i = 0; i <= n; i++) {
    const u = i / n, c = bend * len * Math.sin(Math.PI * u), h = (w / 2) * Math.pow(Math.sin(Math.PI * u), 0.6);
    top.push(new THREE.Vector2(u * len, c + h));
    if (i > 0 && i < n) bot.push(new THREE.Vector2(u * len, c - h));
  }
  return new THREE.ShapeGeometry(new THREE.Shape([...top, ...bot.reverse()]));
}
/* A small jagged ink blot. */
export function blotGeometry(seed: number) {
  const pts: THREE.Vector2[] = [], n = 16;
  let s = seed;
  const r = () => { s = (s * 9301 + 49297) % 233280; return s / 233280; };
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2, rad = 0.6 + r() * 0.4 + (i % 4 === 0 ? r() * 0.4 : 0);
    pts.push(new THREE.Vector2(Math.cos(a) * rad, Math.sin(a) * rad));
  }
  return new THREE.ShapeGeometry(new THREE.Shape(pts));
}
const featherGeometry = () => new THREE.SphereGeometry(0.3, 6, 4).scale(0.4, 0.12, 1.1);

export function createImpacts({ spawn, puff, fxMesh, ink, crow }: ImpactKit): Record<string, Impact> {
  const V = new THREE.Vector3(), P = new THREE.Vector3();
  // Knocked-loose bits are hatched and outlined like a unit, not stippled like an fx: a small stippled piece
  // dissolves into the paper at game zoom. Strokes and blots stay fx ink.
  const bit = (geo: THREE.BufferGeometry, color: number, double = false) => mk(geo, color, { team: 'unit', cast: false, double });

  /* Thrown from `from` with `vel`, falling under `g` (negative: lifting) until it lies on the ground; shrinks out over
     its last third. It starts on the camera's side of the victim: thrown from inside the body it stays hidden in it. */
  function fling(key: string, build: () => THREE.Object3D, from: THREE.Vector3, vel: THREE.Vector3, dur: number, g: number, size = 1, spin = 8) {
    spawn(key, build, dur, (it) => {
      (it.p ||= new THREE.Vector3()).copy(from); (it.v ||= new THREE.Vector3()).copy(vel);
      it.p.z += FRONT;
      it.g = g; it.s = size; it.spin = spin; it.ph = Math.random() * 6.3; it.d = dur;
      const drop = it.p.y - 0.22;  // when it lands: from then on it lies where it fell
      it.land = g > 0 ? (it.v.y + Math.sqrt(it.v.y * it.v.y + 2 * g * Math.max(0, drop))) / g : Infinity;
    }, (it, k) => {
      const s = Math.min(k * it.d, it.land), o = it.obj;
      o.position.copy(it.p).addScaledVector(it.v, s);
      o.position.y = Math.max(0.22, o.position.y - 0.5 * it.g * s * s);
      o.rotation.set(it.ph + s * it.spin, it.ph * 2 + s * it.spin * 0.7, 0);
      o.scale.setScalar(it.s * Math.min(1, (1 - k) * 3) + 0.001);
    });
  }
  /* Something light let go at `at`: thrown by `vel`, slowed by the air, sinking with a sway — feathers. */
  function drift(key: string, build: () => THREE.Object3D, at: THREE.Vector3, vel: THREE.Vector3, dur: number, fall: number, size = 1) {
    spawn(key, build, dur, (it) => {
      (it.p ||= new THREE.Vector3()).copy(at); (it.v ||= new THREE.Vector3()).copy(vel);
      it.p.z += FRONT;  // like fling's
      it.ph = Math.random() * 6.3; it.fall = fall; it.s = size; it.d = dur;
    }, (it, k) => {
      const s = k * it.d, go = (1 - Math.exp(-3 * s)) / 3, o = it.obj;
      o.position.copy(it.p).addScaledVector(it.v, go);
      o.position.x += Math.sin(it.ph + k * 11) * 0.08 * k;
      o.position.y = Math.max(0.22, o.position.y - it.fall * s * s);
      o.rotation.set(Math.sin(it.ph + k * 9) * 0.7, it.ph + k * 2, Math.cos(it.ph + k * 8) * 0.5);
      o.scale.setScalar(it.s * (1 - k * k * k) + 0.001);
    });
  }
  /* A stroke facing the camera, centred on `c`, at `a` radians in the picture (0: left to right, + rising). It is
     drawn from its start over the first `draw` of its life (after `delay`), then thins away. `off` shifts it
     sideways in the picture (parallel claw marks). */
  function slash(key: string, geo: () => THREE.BufferGeometry, len: number, color: number, c: THREE.Vector3, a: number, dur: number,
                 { size = 1, draw = 0.25, delay = 0, off = 0 } = {}) {
    spawn(key, () => fxMesh(geo(), color, { double: true }), dur, (it) => {
      const o = it.obj;
      o.position.set(c.x, c.y, c.z + TOWARD);
      o.rotation.set(FACE, 0, a);
      o.translateX(-len / 2 * size); o.translateY(off * size);  // along the turned stroke: its start, then sideways
      it.s = size; it.draw = draw; it.delay = delay;
    }, (it, k) => {
      const u = (k - it.delay) / (1 - it.delay);
      if (u <= 0) { it.obj.scale.set(0.001, 0.001, 1); return; }
      const fade = u < it.draw ? 1 : 1 - Math.pow((u - it.draw) / (1 - it.draw), 1.5);
      it.obj.scale.set(it.s * Math.min(1, u / it.draw) + 0.001, it.s * fade + 0.001, 1);
    });
  }
  /* An ink stroke on the ground from (x, z) out along `ang`: drawn out fast, then thinning away over its second half. */
  function groundStroke(len: number, w: number, x: number, z: number, ang: number, dur: number) {
    spawn('i:ground' + w, () => flat(fxMesh(strokeGeometry(1, w, 0), ink, { double: true }), 0.24), dur, (it) => {
      it.obj.position.set(x, 0.24, z);
      it.obj.rotation.set(-Math.PI / 2, 0, -ang);
      it.len = len;
    }, (it, k) => it.obj.scale.set(it.len * Math.min(1, k * 7) + 0.001, (k < 0.5 ? 1 : 1 - (k - 0.5) * 2) + 0.001, 1));
  }
  /* A crack in the ground: `parts` straight strokes, each from where the last ended at a kink, the first `r0` out from `at`. */
  function crack(at: THREE.Vector3, ang: number, r0: number, parts: number[], dur: number) {
    let x = at.x + Math.cos(ang) * r0, z = at.z + Math.sin(ang) * r0, a = ang;
    for (const len of parts) {
      groundStroke(len, 0.12, x, z, a, dur);
      x += Math.cos(a) * len; z += Math.sin(a) * len;
      a += rnd(-0.7, 0.7);
    }
  }
  /* A heavy blunt blow: low dust, clods flung away from the attacker, the ground cracked under the victim. */
  function blunt(at: THREE.Vector3, d: THREE.Vector3, s: number, cracks: number, clods: number, dust: number) {
    for (let i = 0; i < dust; i++) {
      const a = (i / dust) * Math.PI * 2 + rnd(-0.3, 0.3);
      puff(P.set(at.x + Math.cos(a) * 0.5, 0.25, at.z + Math.sin(a) * 0.5), DUST,
        { size: 1.1 * s, rise: 0.15, dur: 0.5, drift: V.set(Math.cos(a) * 2, 0, Math.sin(a) * 2) });
    }
    const away = Math.atan2(d.z, d.x);
    for (let i = 0; i < clods; i++) {
      const a = away + rnd(-1.3, 1.3), sp = rnd(3, 4.5);
      fling('i:clod', () => bit(new THREE.DodecahedronGeometry(0.13, 0), CLOD), P.set(at.x, 0.45, at.z),
        V.set(Math.cos(a) * sp, rnd(3, 4.5), Math.sin(a) * sp), 0.55, 14, s * rnd(0.8, 1.3));
    }
    const a0 = Math.random() * Math.PI * 2;
    for (let i = 0; i < cracks; i++) crack(at, a0 + (i / cracks) * Math.PI * 2 + rnd(-0.35, 0.35), 0.6 * s, [0.9 * s, 0.7 * s], 0.5);
  }
  function feathers(at: THREE.Vector3, n: number, colors: number[], fall: number, size = 1) {
    for (let i = 0; i < n; i++) {
      const c = colors[i % colors.length]!;
      drift('i:feather' + c, () => bit(featherGeometry(), c, true), at, V.set(rnd(-3, 3), rnd(0.8, 2), rnd(-1, 3)), 0.8, fall, size);
    }
  }
  /* Small things knocked loose by a shot: thrown out and up from chest height around the victim. */
  function burst(key: string, build: () => THREE.Object3D, at: THREE.Vector3, n: number, out: number, up: number, dur: number, g: number, size = 1, spin = 8) {
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2, sp = out * rnd(0.7, 1.2);
      fling(key, build, P.set(at.x, 1.3, at.z), V.set(Math.cos(a) * sp, up * rnd(0.5, 1), Math.sin(a) * sp), dur, g, 1.5 * size * rnd(0.8, 1.2), spin);
    }
  }
  const blots = [7, 19, 31].map(blotGeometry);
  function blot(key: string, x: number, z: number, color: number, size: number, dur: number) {
    const geo = blots[Math.floor(Math.random() * blots.length)]!;
    spawn(key + color + geo.uuid, () => flat(fxMesh(geo, color), 0.23), dur, (it) => {
      it.obj.position.set(x, 0.23, z); it.obj.rotation.z = Math.random() * 6; it.s = size;
    }, (it, k) => it.obj.scale.setScalar(it.s * Math.min(1, k * 10) * (k > 0.6 ? 1 - (k - 0.6) * 2.5 : 1) + 0.001));
  }
  /* Тень's dusk: a dark wisp drawn out, sinking as it thins. */
  function wisp(at: THREE.Vector3, color: number, size: number) {
    const c = shade(color, 0.6), yaw = Math.random() * 6.3;
    spawn('i:wisp' + c, () => fxMesh(new THREE.IcosahedronGeometry(0.2, 1), c), 0.5, (it) => {
      (it.p ||= new THREE.Vector3()).set(at.x + rnd(-0.3, 0.3), 1.3, at.z + FRONT + rnd(-0.3, 0.3)); it.yaw = yaw; it.s = size;
    }, (it, k) => {
      it.obj.position.copy(it.p); it.obj.position.y -= k * 0.9;
      it.obj.rotation.set(0, it.yaw, 0);
      const s = (1 - k) * it.s;
      it.obj.scale.set(0.5 * s + 0.01, 0.3 * s + 0.01, (1.2 + k * 2) * s + 0.01);
    });
  }

  return {
    /* ---- melee ---- */
    rhino(at, d, big) { blunt(at, d, big ? 1.4 : 1, big ? 4 : 3, big ? 4 : 3, 3); },

    /* Дюрер: steel on steel — sparks off the struck side and a quick crossed glint. */
    durer(at, d, big) {
      const s = big ? 1.4 : 1;
      for (let i = 0; i < (big ? 8 : 6); i++) {
        fling('i:spark', () => bit(new THREE.OctahedronGeometry(0.13), SPARK), P.set(at.x - d.x * 0.4, 1.3, at.z - d.z * 0.4),
          V.set(-d.x * rnd(1, 3) + rnd(-2.5, 2.5), rnd(0.5, 3.5), -d.z * rnd(1, 3) + rnd(-2.5, 2.5)), 0.3, 6, s);
      }
      const tilt = rnd(-0.25, 0.25);
      for (const a of [Math.PI / 4, -Math.PI / 4]) {
        spawn('i:glint', () => bit(strokeGeometry(1.6, 0.12, 0).translate(-0.8, 0, 0), STEEL, true), 0.22, (it) => {
          it.obj.position.set(at.x, 1.5, at.z + TOWARD); it.a = a + tilt; it.s = s;
        }, (it, k) => {
          it.obj.rotation.set(FACE, 0, it.a + k * 0.4);
          const g = (k < 0.3 ? k / 0.3 : 1 - (k - 0.3) / 0.7) * it.s;
          it.obj.scale.set(g + 0.001, (1 - k * 0.5) * it.s + 0.001, 1);
        });
      }
    },

    /* Ландскнехт: the zweihänder's follow-through — ink flung on along the swing and spattered in a fan on the
       ground beyond the victim, a small blot where it struck. */
    landsknecht(at, d, big) {
      const s = big ? 1.4 : 1, side = V.set(-d.z, 0, d.x).clone();
      for (let i = 0; i < (big ? 12 : 9); i++) {
        const sp = rnd(3, 5.5), w = rnd(-1.6, 1.6);
        fling('i:inkdrop', () => bit(new THREE.SphereGeometry(0.11, 6, 4), ink), P.set(at.x, 1.0, at.z),
          V.set(d.x * sp + side.x * w, rnd(1, 2.5), d.z * sp + side.z * w), 0.5, 12, s * rnd(0.7, 1.4));
      }
      const away = Math.atan2(d.z, d.x);
      for (let i = 0; i < 5; i++) {
        const a = away + (i - 2) * 0.22 + rnd(-0.08, 0.08), r0 = rnd(0.5, 0.8);
        groundStroke(rnd(0.6, 1.1) * s, 0.14, at.x + Math.cos(a) * r0, at.z + Math.sin(a) * r0, a, 0.5);
      }
      blot('i:blot', at.x + d.x * 0.4, at.z + d.z * 0.4, ink, 0.45 * s, 0.5);
    },

    /* Смерть: one long thin stroke of the scythe across the victim, slightly bowed, there and gone. */
    death(at, d, big) {
      const len = 3.2;
      slash('i:scythe', () => strokeGeometry(len, 0.14, 0.06), len, SCYTHE, P.set(at.x, 1.15, at.z), (d.x >= 0 ? 1 : -1) * 0.45, 0.4,
        { size: big ? 1.35 : 1, draw: 0.2 });
    },

    /* Перевозчик: the oar comes down like into water — pale droplets up and back down, a few chain links glinting. */
    ferryman(at, _d, big) {
      const s = big ? 1.3 : 1;
      for (let i = 0; i < (big ? 14 : 11); i++) {
        const a = Math.random() * Math.PI * 2, sp = rnd(1.4, 2.8);
        fling('i:splash', () => bit(new THREE.SphereGeometry(0.11, 6, 4), SPRAY), P.set(at.x, 0.9, at.z),
          V.set(Math.cos(a) * sp, rnd(3.5, 5), Math.sin(a) * sp), 0.6, 14, s * rnd(0.7, 1.2));
      }
      for (let i = 0; i < 3; i++) {
        const a = Math.random() * Math.PI * 2;
        fling('i:link', () => bit(new THREE.TorusGeometry(0.12, 0.03, 4, 10), CHAIN), P.set(at.x, 1.2, at.z),
          V.set(Math.cos(a) * 1.5, 2, Math.sin(a) * 1.5), 0.35, 8, s, 14);
      }
    },

    /* Дьявол: three claw marks raked across the victim, red and black, embers lifting off them. */
    devil(at, d, big) {
      const s = big ? 1.35 : 1, a = (d.x >= 0 ? -1 : 1) * 0.85;
      for (let i = 0; i < 3; i++) {
        slash('i:claw' + (i % 2), () => strokeGeometry(1.5, 0.13, 0.08), 1.5, i % 2 ? CLAW_BLACK : CLAW_RED, P.set(at.x, 1.1, at.z), a, 0.4,
          { size: s, draw: 0.2, delay: i * 0.08, off: (i - 1) * 0.28 });
      }
      for (let i = 0; i < 3; i++) {
        fling('i:ember', () => bit(new THREE.OctahedronGeometry(0.08), EMBER), P.set(at.x + rnd(-0.4, 0.4), 1.0, at.z + rnd(-0.2, 0.2)),
          V.set(rnd(-0.3, 0.3), rnd(0.6, 1.2), rnd(-0.3, 0.3)), 0.6, -2, s, 6);
      }
    },

    /* ---- summoned creatures ---- */
    /* Сорока: a peck — black and white feathers knocked loose. */
    magpie(at) { feathers(P.set(at.x, 1.3, at.z), 3, [0x22201e, 0xe8e4da], 0.6); },

    /* Волк: a bite — two curved fang strokes snapping shut from both sides. */
    wolf(at, _d, big) {
      const s = big ? 1.3 : 1;
      for (const jaw of [1, -1]) {
        spawn('i:fang', () => fxMesh(strokeGeometry(1.4, 0.16, -0.25).translate(-0.7, 0, 0), ink, { double: true }), 0.32, (it) => {
          (it.p ||= new THREE.Vector3()).set(at.x, 1.0, at.z + TOWARD); it.jaw = jaw; it.s = s;
        }, (it, k) => {
          // from either side of the victim, where the paper shows them, not over the body
          const shut = Math.min(1, k / 0.35), gap = 0.3 + 0.9 * (1 - shut * shut);
          it.obj.position.copy(it.p);
          it.obj.rotation.set(FACE, 0, it.jaw * Math.PI / 2);
          it.obj.translateY(gap * it.s);  // after the turn: away from the other jaw in the picture
          const fade = k < 0.5 ? 1 : 1 - (k - 0.5) * 2;
          it.obj.scale.set(it.s, it.s * fade + 0.001, 1);
        });
      }
    },

    /* Голем: a clay fist — chips of clay and a little dust. */
    golem(at, d, big) {
      const s = big ? 1.3 : 1, away = Math.atan2(d.z, d.x);
      for (let i = 0; i < 4; i++) {
        const a = away + rnd(-1.1, 1.1), sp = rnd(2.2, 3.5);
        fling('i:chip', () => bit(new THREE.BoxGeometry(0.12, 0.08, 0.1), CLAY), P.set(at.x, 0.9, at.z),
          V.set(Math.cos(a) * sp, rnd(2, 3.5), Math.sin(a) * sp), 0.5, 12, s * rnd(0.8, 1.3), 10);
      }
      for (let i = 0; i < 2; i++) puff(P.set(at.x, 0.6, at.z + FRONT), 0xb8a888, { size: 0.7 * s, rise: 0.3, dur: 0.45 });
    },

    /* ---- neutral monsters ---- */
    /* Бегемот: the rhino's blow, bigger — wider dust, more cracks, heavier clods. */
    behemoth(at, d) { blunt(at, d, 1.6, 5, 2, 5); },

    /* Грифон: talons — two scratches and tawny feathers torn out. */
    griffin(at, d) {
      const a = (d.x >= 0 ? 1 : -1) * 0.9;
      for (let i = 0; i < 2; i++) {
        slash('i:talon', () => strokeGeometry(0.85, 0.09, -0.1), 0.85, 0x3a2a1a, P.set(at.x, 1.2, at.z), a, 0.4, { draw: 0.2, delay: i * 0.06, off: (i - 0.5) * 0.26 });
      }
      feathers(P.set(at.x, 1.5, at.z), 4, [TAWNY, 0xe0d4b8], 0.5, 1.2);
    },

    /* ---- shooters: small, in the shot's own colour ---- */
    arquebusier(at, _d, big) {
      for (let i = 0; i < 2; i++) puff(P.set(at.x, 1.2, at.z + FRONT), 0xd0c7b3, { size: big ? 2.2 : 1.7, rise: 0.4, dur: 0.45 });
      burst('i:lead', () => bit(new THREE.TetrahedronGeometry(0.13), 0x4a4a50), at, big ? 4 : 3, 3, 2, 0.4, 12);
    },
    astrologer(at, _d, big, color) {
      burst('i:shard' + color, () => bit(new THREE.OctahedronGeometry(0.13), color), at, big ? 7 : 5, 4, 1.5, 0.35, 2, big ? 1.3 : 1, 12);
    },
    // Her flame lands as a stream of skill ticks: kept light and the same size whether `big` or not.
    salamander(at, _d, _big, color) {
      burst('i:cinder' + color, () => bit(new THREE.OctahedronGeometry(0.12), color), at, 4, 2.4, 3, 0.45, 3);
      puff(P.set(at.x, 1.3, at.z + FRONT), 0x6a6058, { size: 1.3, rise: 0.6, dur: 0.4 });
    },
    monk(at, _d, big, color) {
      burst('i:mote' + color, () => bit(new THREE.SphereGeometry(0.12, 6, 4), color), at, big ? 6 : 4, 1.4, 1.2, 0.6, -1.5);
    },
    plague(at, _d, big, color) {
      for (let i = 0; i < (big ? 3 : 2); i++) puff(P.set(at.x, 1.2, at.z + FRONT), shade(color, 0.8), { size: 2, rise: 0.7, dur: 0.6 });
    },
    grotesque(at, _d, big, color) {
      burst('i:bile' + color, () => bit(new THREE.SphereGeometry(0.12, 6, 4), color), at, big ? 6 : 4, 2, 1.5, 0.5, 12);
      blot('i:bileBlot', at.x, at.z + FRONT, shade(color, 0.7), big ? 0.7 : 0.5, 0.6);
    },
    sin(at, _d, big, color) {
      burst('i:crimson' + color, () => bit(new THREE.SphereGeometry(0.12, 6, 4), color), at, big ? 6 : 4, 2.2, 1.8, 0.5, 12);
    },
    puppeteer(at, _d, big) {
      burst('i:splinter', () => bit(new THREE.BoxGeometry(0.07, 0.07, 0.4), 0xd8c094), at, big ? 6 : 4, 3, 2.2, 0.45, 10, 1, 16);
    },
    nightmare(at, _d, big) { feathers(P.set(at.x, 1.3, at.z), big ? 5 : 3, [crow], 0.8); },
    shewolf(at, _d, big) {
      burst('i:fleck', () => bit(new THREE.TetrahedronGeometry(0.12), ink), at, big ? 7 : 5, 3, 1.5, 0.45, 10);
    },
    shadow(at, _d, big, color) { for (let i = 0; i < (big ? 3 : 2); i++) wisp(at, color, 1.4); },
    birdcatcher(at, _d, big, color) { feathers(P.set(at.x, 1.3, at.z), big ? 3 : 2, [color], 0.6); },
  };
}
